// Выбор в сопоставлении — порт плеера (academy-frontend `blocks/hooks/matchPairs.ts`):
// карточки не двигаются, пару «термин — описание» собирают тапами и отмечают
// цветом. Идентификатор карточки — индекс её пары в блоке (у правой — пары,
// чьё описание на ней написано).
import type { QuizOptionState } from '@teach/ui'

export type MatchSide = 'left' | 'right'

export interface MatchSelection {
  /** Собранные пары: термин (индекс) → описание (индекс). */
  pairs: Record<number, number>
  /** Карточка, которая ждёт пару. */
  pending: { side: MatchSide; id: number } | null
}

/** Пары без той, в которой стоит карточка (side, id). */
const withoutPairOf = (
  pairs: Record<number, number>,
  side: MatchSide,
  id: number,
): Record<number, number> =>
  Object.fromEntries(
    Object.entries(pairs).filter(([left, right]) =>
      side === 'left' ? Number(left) !== id : right !== id,
    ),
  )

/**
 * Тап по карточке. Карточка из пары — пара разбивается, карточка ждёт новую.
 * Ждёт карточка из другой колонки — собираем пару. Повторный тап по ждущей —
 * снимаем выбор. Иначе — карточка ждёт пару.
 */
export const pickMatchItem = (
  selection: MatchSelection,
  side: MatchSide,
  id: number,
): MatchSelection => {
  const { pending } = selection
  const pairs = withoutPairOf(selection.pairs, side, id)

  if (pending && pending.side !== side) {
    const [left, right] = side === 'left' ? [id, pending.id] : [pending.id, id]
    return { pairs: { ...pairs, [left]: right }, pending: null }
  }
  if (pending?.side === side && pending.id === id) return { pairs, pending: null }
  return { pairs, pending: { side, id } }
}

const pairedLeft = (pairs: Record<number, number>, right: number): number | undefined => {
  const left = Object.keys(pairs).find((l) => pairs[Number(l)] === right)
  return left === undefined ? undefined : Number(left)
}

/**
 * Вид карточки: состояние (ждёт пару / верно / неверно) и номер пары — по
 * месту термина, чтобы цвет пары не прыгал при пересборке. `graded` — ответ
 * проверен: карточка в верной паре зеленеет, остальные краснеют.
 */
export const matchCardView = (
  selection: MatchSelection,
  graded: boolean,
  side: MatchSide,
  id: number,
): { state: QuizOptionState; pairIndex?: number } => {
  const { pairs, pending } = selection
  const left = side === 'left' ? id : pairedLeft(pairs, id)
  const pairIndex = left !== undefined && pairs[left] !== undefined ? left : undefined

  if (graded)
    return {
      state: left !== undefined && pairs[left] === left ? 'correct' : 'incorrect',
      pairIndex,
    }
  const isPending = pending?.side === side && pending.id === id
  return { state: isPending ? 'selected' : 'idle', pairIndex }
}
